import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from '../../icons';
import { Button, Input, Select, Textarea, TimePicker } from '../../components/shared';
// S6 (2026-09-16) — 휴가 기안의 반차 · 반반차 «시작 시각»: 조건부 칸(showWhen) · 그날 근무제 · 기간 하루 잠금.
import { getWorkPolicy, type WorkPolicyDto } from '../../api/hr-schedule';
import {
  checkStart, durationText, endTimeFor, isPartialLeaveType, partialLeaveMinutes, startWindow, unavailableText,
  type PartialLeaveKind,
} from '../../lib/partial-leave-time';
import '../DashboardPage/modals/partial-leave-time.css';
import { fetchCodeOptions, fetchDivisionNames, fetchTeamNames } from '../../api/approval-form-sources';
import { selectCode, selectLabel, type SelectOption } from '../../lib/approval-select';
import type { FieldSource } from '../../types/approval';
import type { ApprovalFormField } from '../../types/approval';
import { computeFormula, readNumber, type FormulaResult } from '../../lib/approval-formula';
import './ApprovalForm.css';

/**
 * 서식 칸 — **기안 화면이 그리는 자리** (2026-08-28 · 자리 재정의 판 9).
 *
 * ★★ **없으면 종전과 똑같다.** 서식이 없는 종류는 이 컴포넌트가 아무것도 그리지 않는다 —
 *    제목·본문만 받던 화면 그대로다.
 *
 * ★★ **「고르기」의 목록을 여기서 만들지 않는다.** 휴가 종류는 근태 기준 설정이, 부서는
 *    조직도가 주인이다. 서식은 주인을 가리키기만 하고, 이 화면이 **그 주인에게 물어본다** —
 *    목록을 여기 베껴 두면 한 값을 두 자리에서 고치게 된다.
 *
 * ★ **주인을 못 부르면 빈 목록**이고 그렇게 말한다(기준 설정 서버가 아직 없을 수 있다).
 *   그때도 필수가 아니면 기안은 된다 — 못 고르는 칸 하나가 결재를 통째로 막지 않는다.
 */

export type FormValues = Record<string, unknown>;

interface Props {
  fields: ApprovalFormField[];
  value: FormValues;
  onChange: (next: FormValues) => void;
  disabled?: boolean;
}

/**
 * 조건부 칸이 지금 서는가 (S6 · 계약 §9-1) — `showWhen.field` 칸의 **코드**가 목록 안에 있을 때만.
 * ★ 고르기 칸은 `{code,label}` 을 담는다 — 회사가 이름을 바꿔도 코드는 그대로다.
 */
export function isFieldShown(field: ApprovalFormField, values: FormValues): boolean {
  if (!field.showWhen) return true;
  return field.showWhen.in.includes(selectCode(values[field.showWhen.field]));
}

/**
 * 값 정리 — ① 숨은 칸의 값은 버린다: 반반차에서 고른 시각이 연차로 바꾼 뒤에도 남아 가면 서버가 400 이다
 * (`LEAVE_TIME_NOT_ALLOWED`). ② 휴가 종류가 반차 · 반반차면 기간을 하루로 잠근다(서버 `INVALID_DATE_RANGE` 와 같은 뜻).
 */
export function tidyFormValues(fields: ApprovalFormField[], values: FormValues): FormValues {
  const next: FormValues = { ...values };
  for (const f of fields) {
    if (!isFieldShown(f, next) && f.key in next) delete next[f.key];
  }
  const period = next.period;
  if (isPartialLeaveType(selectCode(next.leave_type)) && typeof period === 'object' && period !== null) {
    const p = period as { start?: string; end?: string };
    if (p.start && p.end !== p.start) next.period = { ...p, end: p.start };
  }
  return next;
}

export function ApprovalFormFields({ fields, value, onChange, disabled = false }: Props) {
  if (fields.length === 0) return null;
  const set = (key: string, v: unknown) => onChange(tidyFormValues(fields, { ...value, [key]: v }));
  const leaveCode = selectCode(value.leave_type);
  const partialLeave = isPartialLeaveType(leaveCode) ? leaveCode : null;
  const periodStart = typeof value.period === 'object' && value.period !== null
    ? (value.period as { start?: string }).start
    : undefined;
  // 양식에 「끝 시각」 칸이 따로 서면 시작 칸은 끝을 겹쳐 적지 않는다(서버 기본 양식은 둘 다 둔다 · 회사 사본은 없을 수 있다).
  const hasEndField = fields.some((x) => x.key === 'end_time' && isFieldShown(x, value));

  /**
   * ★★★ **계산식은 여기서 «보여 주기 위해» 센다** (2026-09-01 · 판 13).
   *
   *    저장되는 값은 **서버가 다시 센다** — 전결을 가르는 숫자를 브라우저가 정하게 두면
   *    결재선을 짧게 만든 요청을 그대로 받게 된다. 그래서 이 값은 화면에만 쓰고 보내지 않는다.
   *    규칙이 갈리지 않도록 서버와 **같은 조각**을 쓴다(`approval-formula.sync.test.ts` 가 잠근다).
   */
  const calc = (f: ApprovalFormField): FormulaResult => computeFormula(f.expr ?? '', {
    field: (key) => value[key],
    column: (fieldKey, column) => {
      const t = fields.find((x) => x.key === fieldKey);
      const idx = (t?.columns ?? []).indexOf(column);
      const rows = value[fieldKey];
      if (idx < 0 || !Array.isArray(rows)) return [];
      // ★ 고르기 열은 짝이다 — 이름으로 읽는다(서버와 같은 규칙 · 예산 판 B0).
      return (rows as unknown[]).map((r) => (Array.isArray(r) ? selectLabel(r[idx]) : ''));
    },
  });

  return (
    <div className="apf">
      {fields.filter((f) => isFieldShown(f, value)).map((f) => (
        <div key={f.key} className={`apf__field apf__field--${f.kind}`}>
          {/*
            ★★ **`<label>` 로 감싸지 않는다.** 감싸면 안에 있는 **모든** 컨트롤이 이 이름표를
               가져간다 — 기간(칸 둘)·표(칸 여럿과 단추들)에서 「줄 추가」 단추의 이름이
               「내역」이 되어 버린다. 그래서 이름표는 `aria-labelledby` 로 **하나에만** 걸고,
               여러 칸을 가진 종류는 제 칸마다 이름을 따로 붙인다.
          */}
          <span className="apf__label" id={`apf-${f.key}`}>
            {f.label}
            {f.required && <span className="apf__req" aria-label="필수"> *</span>}
            {/* ★ 전결 기준으로 쓰는 칸임을 밝힌다 — 이 숫자가 결재 단계를 바꾼다. */}
            {f.measure && <span className="apf__measure">전결 기준</span>}
          </span>
          {f.kind === 'formula'
            ? <FormulaControl field={f} result={calc(f)} />
            : f.kind === 'time' && f.showWhen?.field === 'leave_type' && partialLeave && f.key === 'end_time'
              // ★ 끝 시각은 서버가 계산해 채운다(계약 §5) — 고르는 칸이 아니라 읽는 줄이다.
              ? <LeaveEndTimeReadout kind={partialLeave} date={periodStart} start={value.start_time} />
              : f.kind === 'time' && f.showWhen?.field === 'leave_type' && partialLeave
                ? (
                  <LeaveStartTimeControl
                    label={f.label} kind={partialLeave} date={periodStart}
                    value={value[f.key]} onChange={(v) => set(f.key, v)} disabled={disabled}
                    showEnd={!hasEndField}
                  />
                )
              : f.kind === 'period' && partialLeave
                ? <PeriodControl value={value[f.key]} onChange={(v) => set(f.key, v)} disabled={disabled} singleDay />
                : <Control field={f} value={value[f.key]} onChange={(v) => set(f.key, v)} disabled={disabled} />}
          {f.hint && <span className="apf__hint">{f.hint}</span>}
        </div>
      ))}
    </div>
  );
}

function Control({ field, value, onChange, disabled }: {
  field: ApprovalFormField;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled: boolean;
}) {
  // ★ 칸 하나짜리 종류는 위의 이름표를 가리킨다(기간·표는 제 칸마다 이름이 따로 있다).
  const common = {
    disabled, fullWidth: true, required: field.required,
    'aria-labelledby': `apf-${field.key}`,
  };

  switch (field.kind) {
    case 'textarea':
      return (
        <Textarea
          {...common}
          rows={4}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return (
        <Input
          {...common}
          type="number"
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    case 'date':
      return (
        <Input {...common} type="date" value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)} />
      );
    /* ★ 시각 — 서버가 `HH:MM` 만 받는다. 브라우저가 그 모양으로 주므로 여기서 고칠 것이 없다. */
    case 'time':
      return (
        <Input {...common} type="time" value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)} />
      );
    case 'period':
      return <PeriodControl value={value} onChange={onChange} disabled={disabled} />;
    case 'select':
      return <SelectControl field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'table':
      return <TableControl field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'user':
    case 'text':
    default:
      return (
        <Input {...common} type="text" value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)} />
      );
  }
}

/**
 * 계산식 — **못 적는 칸**. 값을 보여 주고, 그 값이 어디서 왔는지 식을 함께 남긴다.
 *
 * ★★ **모르면 「—」다. 0 이 아니다.** 표가 비었거나 시각 한쪽이 안 적혔으면 아직 잴 수 없다.
 *    전결 기준 칸이 그러면 **결재선을 줄이지 않는다**(서버도 같은 판단을 한다).
 * ★★ **못 읽은 칸을 숨기지 않는다** — 「3줄 중 1줄을 못 읽었습니다」를 적는다.
 *    조용히 빼면 합계가 조용히 틀린다.
 */
function FormulaControl({ field, result }: { field: ApprovalFormField; result: FormulaResult }) {
  const d = field.decimals ?? 0;
  const shown = result.value == null
    ? '—'
    : field.format === 'money'
      ? `${result.value.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })} 원`
      : field.format === 'hours'
        ? `${result.value.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })} 시간`
        : result.value.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

  return (
    <>
      <output className="apf__calc" aria-labelledby={`apf-${field.key}`}>
        <b className={result.value == null ? 'apf__calc--none' : undefined}>{shown}</b>
        <code className="apf__calcexpr">{field.expr}</code>
      </output>
      {result.value == null && (
        <span className="apf__hint">아직 잴 수 없습니다 — 위 칸을 채우면 여기에 값이 뜹니다.</span>
      )}
      {result.unread > 0 && (
        <span className="apf__warn">
          {result.read}칸 중 {result.unread}칸을 숫자로 읽지 못해 셈에서 빠졌습니다.
        </span>
      )}
    </>
  );
}

/**
 * 기간 — 두 날짜가 한 칸이다. 끝이 시작보다 빠르면 서버가 막으므로 시작을 하한으로 준다.
 * `singleDay`(S6) — 반차 · 반반차는 하루 안이다: 종료일을 잠그고 시작일을 따라가게 한다.
 */
function PeriodControl({ value, onChange, disabled, singleDay = false }: {
  value: unknown; onChange: (v: unknown) => void; disabled: boolean; singleDay?: boolean;
}) {
  const v = (typeof value === 'object' && value !== null ? value : {}) as { start?: string; end?: string };
  return (
    <span className="apf__period">
      <Input
        wrapperClassName="apf__periodin" type="date" disabled={disabled} value={v.start ?? ''}
        aria-label="시작일"
        onChange={(e) => onChange(singleDay ? { ...v, start: e.target.value, end: e.target.value } : { ...v, start: e.target.value })}
      />
      <span className="apf__tilde">~</span>
      <Input
        wrapperClassName="apf__periodin" type="date" disabled={disabled || singleDay}
        value={singleDay ? (v.start ?? '') : (v.end ?? '')}
        min={v.start || undefined}
        aria-label="종료일"
        onChange={(e) => onChange({ ...v, end: e.target.value })}
      />
    </span>
  );
}

/**
 * 휴가 기안의 반차 · 반반차 «시작 시각» (S6) — 근태 신청 창과 같은 규칙 · 같은 순수 함수.
 *
 * ★ 범위 · 눈금 · 길이는 **기안자의 그날 근무제**(`partialLeave`)가 준다 — 기간의 날짜가 바뀌면 다시 받는다.
 * ★ 끝 시각은 보내지 않는다(서버가 계산해 문서에 채운다) — 여기서는 읽는 줄로만 보인다.
 * ★ 날짜가 바뀌어 고른 시각이 범위를 벗어나면 비운다 — 틀린 시각이 그대로 상신되지 않게.
 */
/** 기안자의 그날 근무제 — 받는 동안 `undefined` · 못 받으면 `null`. 시작 · 끝 두 칸이 같은 날짜로 부른다. */
function usePartialLeavePolicy(date: string | undefined): WorkPolicyDto | null | undefined {
  const [policy, setPolicy] = useState<WorkPolicyDto | null | undefined>(undefined);
  useEffect(() => {
    setPolicy(undefined);
    if (!date) return;
    let alive = true;
    getWorkPolicy(date)
      .then((p) => { if (alive) setPolicy(p); })
      .catch(() => { if (alive) setPolicy(null); });
    return () => { alive = false; };
  }, [date]);
  return policy;
}

/** 「끝 시각」 칸 — 서버가 채우는 값의 미리 보기(시작 + 그날 소정 비례 길이). 보내지 않는다. */
function LeaveEndTimeReadout({ kind, date, start }: { kind: PartialLeaveKind; date: string | undefined; start: unknown }) {
  const policy = usePartialLeavePolicy(date);
  const partial = policy?.partialLeave ?? null;
  const startText = typeof start === 'string' && start !== '' ? start : null;
  const end = partial ? endTimeFor(partial, kind, startText) : null;
  return (
    <span className="dw-ptime__end" aria-live="polite">
      {end ? <b>{end}</b> : <span className="apf__hint">시작 시각을 고르면 정해집니다</span>}
      <span className="dw-ptime__tag">자동</span>
      {partial && <small>끝 = 시작 + {durationText(partialLeaveMinutes(partial, kind))}</small>}
    </span>
  );
}

function LeaveStartTimeControl({ label, kind, date, value, onChange, disabled, showEnd = true }: {
  label: string; kind: PartialLeaveKind; date: string | undefined;
  value: unknown; onChange: (v: unknown) => void; disabled: boolean;
  /** 양식에 「끝 시각」 칸이 없을 때만 여기서 끝을 함께 적는다. */
  showEnd?: boolean;
}) {
  const policy = usePartialLeavePolicy(date);
  const partial = policy?.partialLeave ?? null;
  const start = typeof value === 'string' && value !== '' ? value : null;
  const out = partial != null && start != null && checkStart(partial, kind, start) !== 'ok';
  useEffect(() => {
    if (out) onChange('');
    // onChange 는 매 렌더 새 함수다 — 범위를 벗어난 «그 순간»에만 비우면 된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [out]);

  if (!date) return <span className="apf__hint">기간(날짜)을 먼저 고르면 시작 시각을 고를 수 있습니다.</span>;
  if (policy === undefined) return <span className="apf__hint">그날 근무 시간을 불러오는 중…</span>;
  if (!partial) return <div className="dw-ptime__blocked" role="note">그날 근무제를 읽지 못해 시작 시각을 고를 수 없습니다. 잠시 뒤 다시 시도해 주세요.</div>;
  if (!partial.available) return <div className="dw-ptime__blocked" role="note">{unavailableText(partial.reason)}</div>;
  const win = startWindow(partial, kind);
  if (!win) {
    return <div className="dw-ptime__blocked" role="note">그날 근무 시간({partial.earliestStart}–{partial.latestEnd})에는 고를 수 있는 시작 시각이 없습니다.</div>;
  }
  const end = endTimeFor(partial, kind, start);
  return (
    <span className="apf__ptime">
      <TimePicker
        ariaLabel={label} value={start} onChange={(v) => onChange(v ?? '')}
        minuteStep={partial.stepMinutes} min={win.min} max={win.max} disabled={disabled}
      />
      {showEnd ? (
        <span className="dw-ptime__end" aria-live="polite">
          {end ? <b>~ {end}</b> : null}
          <span className="dw-ptime__tag">자동</span>
          <small>끝 = 시작 + {durationText(partialLeaveMinutes(partial, kind))} · 근무 시간 {partial.earliestStart}–{partial.latestEnd}</small>
        </span>
      ) : (
        <span className="apf__hint">근무 시간 {partial.earliestStart}–{partial.latestEnd} · 시작 {win.min}–{win.max}</span>
      )}
    </span>
  );
}

/**
 * 고르기 — 목록을 **주인에게서** 받고, **코드와 이름을 함께** 담는다 (2026-09-04 · 예산 판 B0).
 *
 * ★★★ **담는 값이 바뀌었다**: `"연차"` → `{ code: 'annual', label: '연차' }`.
 *      코드는 붙이는 데(예산·근태) 쓰고, 이름은 **그때 본 그대로** 남는다.
 * ★★ **옛 값(이름 문자열)도 그대로 연다** — 이미 저장된 문서가 그렇다. `selectCode` 로
 *     지금 고른 것을 찾고, 목록 밖이면 그 값을 **지우지 않고 남긴다**.
 * ★ 붙박이 목록(`inline`)은 코드가 따로 없다 — **이름이 곧 코드**다(서식 편집기가 낱말만 받는다).
 */
function SelectControl({ field, value, onChange, disabled }: {
  field: ApprovalFormField; value: unknown; onChange: (v: unknown) => void; disabled: boolean;
}) {
  const [remote, setRemote] = useState<SelectOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  const src = field.source;

  const inline = useMemo(
    // 붙박이는 낱말만 있다 — 코드 자리에 그 낱말을 그대로 둔다.
    () => (src?.kind === 'inline' ? src.options.map((o) => ({ code: o, label: o })) : null),
    [src],
  );

  useEffect(() => {
    if (!src || src.kind === 'inline') return;
    let alive = true;
    const load: Promise<SelectOption[]> = src.kind === 'company_code'
      ? fetchCodeOptions(src.setKey)
      : src.level === 'division' ? fetchDivisionNames() : fetchTeamNames();
    load
      .then((opts) => { if (alive) setRemote(opts); })
      // ★ 주인을 못 부르면 빈 목록이고 그렇게 말한다 — 기안 자체를 막지는 않는다.
      .catch(() => { if (alive) { setRemote([]); setFailed(true); } });
    return () => { alive = false; };
  }, [src]);

  const options = inline ?? remote;
  const current = selectCode(value);
  const currentLabel = selectLabel(value);
  const known = (options ?? []).some((o) => o.code === current);

  /**
   * ★★ **목록 밖의 옛 값은 목록에 «끼워» 준다** — 네이티브 `<select>` 시절 `<option>` 하나를
   *    더 그려 두던 것과 같은 일이다. 정본 `Select` 는 목록에 없는 값을 그릴 수 없어,
   *    안 끼우면 이미 저장된 문서를 열었을 때 **골라 둔 값이 빈칸으로 보인다**.
   */
  const items = [
    ...(current !== '' && !known ? [{ value: current, label: currentLabel || current }] : []),
    ...(options ?? []).map((o) => ({ value: o.code, label: o.label })),
  ];

  return (
    <>
      <Select
        disabled={disabled || options === null}
        value={current === '' ? null : current}
        options={items}
        placeholder={options === null ? '불러오는 중…' : '고르세요'}
        ariaLabel={field.label}
        onChange={(v) => {
          const picked = (options ?? []).find((o) => o.code === v);
          onChange(picked ? { code: picked.code, label: picked.label } : '');
        }}
      />
      {failed && <span className="apf__warn">목록을 불러오지 못했습니다 — 직접 고를 수 없습니다.</span>}
    </>
  );
}

/** 그 열의 종류 — 안 정했으면 글자다(옛 서식이 그대로 도는 자리). */
function colKind(field: ApprovalFormField, ci: number): string {
  return (field.columnKinds ?? [])[ci] ?? 'text';
}

/**
 * 그 열의 목록 주인 — `select` 열만 갖는다 (2026-09-04 · 예산 판 B0).
 */
function colSource(field: ApprovalFormField, ci: number): FieldSource | null {
  return (field.columnSources ?? [])[ci] ?? null;
}

/**
 * 표 안의 **고르기 칸** (2026-09-04 · 예산 판 B0).
 *
 * ★★★ **목록이 비면 글자로 적게 둔다.** 계정과목처럼 «바닥이 빈» 목록이 있어서,
 *      회사가 아직 하나도 안 등록했으면 고를 것이 없다. 그때 고르개만 세우면
 *      **예전에 적을 수 있던 표를 이제 못 적는다**(회귀). 그래서 그 자리는 글자 칸이다.
 * ★★ 값은 고른 순간 **`{code,label}`** 이 된다 — 손으로 적은 옛 값(이름)도 그대로 읽는다.
 */
function TableSelectCell({ src, value, disabled, label, onChange }: {
  src: FieldSource; value: unknown; disabled: boolean; label: string;
  onChange: (v: unknown) => void;
}) {
  const [remote, setRemote] = useState<SelectOption[] | null>(null);
  const inline = useMemo(
    () => (src.kind === 'inline' ? src.options.map((o) => ({ code: o, label: o })) : null),
    [src],
  );

  useEffect(() => {
    if (src.kind === 'inline') return;
    let alive = true;
    const load: Promise<SelectOption[]> = src.kind === 'company_code'
      ? fetchCodeOptions(src.setKey)
      : src.level === 'division' ? fetchDivisionNames() : fetchTeamNames();
    load
      .then((opts) => { if (alive) setRemote(opts); })
      .catch(() => { if (alive) setRemote([]); });
    return () => { alive = false; };
  }, [src]);

  const options = inline ?? remote;
  const code = selectCode(value);
  const text = selectLabel(value);

  // ★★★ 아직 못 불렀거나(=null) 목록이 비었으면 **적을 수 있게** 둔다.
  if (options === null || options.length === 0) {
    return (
      <Input
        fullWidth disabled={disabled} value={text} type="text"
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const known = options.some((o) => o.code === code);
  // 손으로 적어 둔 옛 값은 남긴다 — 지우면 저장하는 순간 조용히 사라진다.
  const items = [
    ...(code !== '' && !known ? [{ value: code, label: text || code }] : []),
    ...options.map((o) => ({ value: o.code, label: o.label })),
  ];
  return (
    <Select
      disabled={disabled}
      value={code === '' ? null : code}
      options={items}
      placeholder="고르세요"
      ariaLabel={label}
      /* ★★★ 표는 `overflow` 상자다 — 안 켜면 목록이 그 상자에 «잘린다». */
      portal
      onChange={(v) => {
        const picked = options.find((o) => o.code === v);
        onChange(picked ? { code: picked.code, label: picked.label } : '');
      }}
    />
  );
}

/** 표 — 열은 서식이 정하고, 줄은 기안자가 늘린다. */
function TableControl({ field, value, onChange, disabled }: {
  field: ApprovalFormField; value: unknown; onChange: (v: unknown) => void; disabled: boolean;
}) {
  const cols = field.columns ?? [];
  /**
   * ★★ 칸 값은 **글자, 또는 고르기 짝**이다 (2026-09-04 · 예산 판 B0).
   *    예전처럼 `String(c)` 로 굳히면 짝이 `[object Object]` 가 된다 — 굳히지 않고 그대로 이고 간다.
   */
  const rows: unknown[][] = Array.isArray(value)
    ? (value as unknown[]).map((r) => (Array.isArray(r) ? [...r] : []))
    : [];

  const setCell = (ri: number, ci: number, v: unknown) => {
    const next = rows.map((r) => [...r]);
    while (next.length <= ri) next.push(Array.from({ length: cols.length }, () => ''));
    while (next[ri]!.length < cols.length) next[ri]!.push('');
    next[ri]![ci] = v;
    onChange(next);
  };

  return (
    <span className="apf__table">
      <table>
        <thead>
          <tr>{cols.map((c) => <th key={c} scope="col">{c}</th>)}<th aria-label="줄 지우기" /></tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {cols.map((c, ci) => (
                <td key={c}>
                  {/*
                    ★★ **열 종류대로 그린다** (2026-09-01 · 판 13) — 「금액」 열에 날짜 고르개가
                       뜨면 안 되고, 숫자 열은 숫자판이 떠야 한다. 종류가 없으면 전부 글자다
                       (= 옛 서식의 모습).
                    ★ 값은 종류와 무관하게 **문자열 그대로** 담는다 — 담는 모양을 바꾸면 이미
                      올라간 문서를 못 읽는다.
                  */}
                  {colKind(field, ci) === 'select' && colSource(field, ci) ? (
                    <TableSelectCell
                      src={colSource(field, ci)!} value={r[ci]} disabled={disabled}
                      label={`${ri + 1}번째 줄 ${c}`}
                      onChange={(v) => setCell(ri, ci, v)}
                    />
                  ) : (
                    <Input
                      fullWidth disabled={disabled} value={selectLabel(r[ci])}
                      type={colKind(field, ci) === 'date' ? 'date' : 'text'}
                      inputMode={colKind(field, ci) === 'number' || colKind(field, ci) === 'money' ? 'decimal' : undefined}
                      aria-label={`${ri + 1}번째 줄 ${c}`}
                      onChange={(e) => setCell(ri, ci, e.target.value)}
                    />
                  )}
                </td>
              ))}
              <td>
                <Button
                  variant="quiet-danger" size="sm" iconOnly disabled={disabled}
                  aria-label={`${ri + 1}번째 줄 지우기`}
                  onClick={() => onChange(rows.filter((_, i) => i !== ri))}
                  iconLeft={<Trash2 size={15} aria-hidden />}
                />
              </td>
            </tr>
          ))}
        </tbody>
        {/*
          ★★ **숫자 열에는 합을 적어 준다** (2026-09-01 · 판 13). 계산식이 세는 값과 같은 셈이지만,
             기안자는 표를 보면서 바로 확인하고 싶어 한다 — 「내가 적은 것이 맞나」에 답하는 자리다.
          ★ 못 읽은 칸은 **빼고 세되 그 사실을 위 계산식 칸이 말한다** — 여기서 또 말하면 시끄럽다.
        */}
        {rows.length > 0 && (field.columnKinds ?? []).some((k) => k === 'number' || k === 'money') && (
          <tfoot>
            <tr>
              {cols.map((c, ci) => {
                const k = colKind(field, ci);
                if (k !== 'number' && k !== 'money') return <td key={c}>{ci === 0 ? '합계' : ''}</td>;
                let sum = 0; let any = false;
                for (const r of rows) {
                  const v = readNumber(selectLabel(r[ci]));
                  if (v != null) { sum += v; any = true; }
                }
                return <td key={c} className="apf__sum">{any ? sum.toLocaleString() : '—'}</td>;
              })}
              <td />
            </tr>
          </tfoot>
        )}
      </table>
      <Button
        variant="secondary" size="sm" className="apf__rowadd" disabled={disabled}
        onClick={() => onChange([...rows, Array.from({ length: cols.length }, () => '')])}
        iconLeft={<Plus size={14} aria-hidden />}
      >
        줄 추가
      </Button>
    </span>
  );
}
